import CryptoJS from 'crypto-js';

const STORAGE_KEY = 'ats_llm_settings_v2'; // Changed key for v2 migration
const LEGACY_STORAGE_KEY = 'ats_llm_settings';
const DEFAULT_SECRET = 'ats_secret_key_123'; 

export interface LLMConfig {
    id: string; // Unique ID
    name: string; // User friendly name e.g. "My Groq"
    provider: string; // 'groq' | 'openai' | 'anthropic' | 'gemini' | 'openrouter'
    api_key: string;  // Plain text in memory, encrypted in storage
    base_url?: string;
    model?: string;
    is_default: boolean;
}

export interface GlobalSettings {
    encryption_secret: string;
    configs: LLMConfig[];
}

export const getStoredSettings = (): GlobalSettings => {
    if (typeof window === 'undefined') return { encryption_secret: '', configs: [] };
    
    // Try v2 first
    const data = localStorage.getItem(STORAGE_KEY);
    
    if (data) {
        try {
            return JSON.parse(data);
        } catch (e) {
            console.error("Failed to parse settings", e);
            return { encryption_secret: '', configs: [] };
        }
    }

    // Migration Check: Try legacy v1
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy) {
        try {
            const parsed = JSON.parse(legacy);
            // Migrate to new format
            const migrated: GlobalSettings = {
                encryption_secret: parsed.encryption_secret || '',
                configs: []
            };

            if (parsed.api_key) {
                // We need to decrypt the old key if we want to re-save it, OR just move the encrypted blob?
                // The interface expects `api_key` to be handled carefully. 
                // Let's assume we just port it over.
                // Wait, `LLMConfig.api_key` in memory should be PLAIN.
                // In storage it is ENCRYPTED.
                // The `parsed` object from legacy storage has ENCRYPTED key in `api_key` field.
                
                // We need to decrypt it to put it into memory state if we want consistent behavior, 
                // but `getStoredSettings` usually returns the raw storage object?
                // NO. references to `getStoredSettings` in UI expect to see the data. 
                // In `SettingsModal` (v1), we decrypted it in `useEffect`.
                
                // Let's standardise: `getStoredSettings` returns data ready for UI (so decrypted keys).
                // `saveSettings` takes data from UI and encrypts it.
                
                const secret = parsed.encryption_secret || '';
                const plainKey = parsed.api_key ? decryptData(parsed.api_key, secret) : '';
                
                if (plainKey) {
                   migrated.configs.push({
                       id: Date.now().toString(),
                       name: 'Default Provider',
                       provider: parsed.provider,
                       api_key: plainKey,
                       base_url: parsed.base_url,
                       model: parsed.model,
                       is_default: true
                   });
                }
            }
            return migrated;
        } catch (e) {
            console.error("Migration failed", e);
        }
    }

    return { encryption_secret: '', configs: [] };
};

export const saveSettings = (settings: GlobalSettings) => {
    if (typeof window === 'undefined') return;
    
    // Deep copy to avoid mutating UI state
    const storageData = JSON.parse(JSON.stringify(settings));
    const secret = settings.encryption_secret || '';

    // Encrypt all keys
    storageData.configs = storageData.configs.map((c: LLMConfig) => ({
        ...c,
        api_key: encryptData(c.api_key, secret) // Encrypt!
    }));

    localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
};

// Returns the ready-to-use config for backend (encrypted key)
export const getDefaultConfig = () => {
    const settings = getStoredSettings(); // Returns decrypted keys
    const secret = settings.encryption_secret || DEFAULT_SECRET;
    
    const def = settings.configs.find(c => c.is_default) || settings.configs[0];
    
    if (!def) return null;

    return {
        provider: def.provider,
        api_key: encryptData(def.api_key, secret), // Re-encrypt for transport
        base_url: def.base_url,
        model: def.model
    };
};

export const encryptData = (text: string, secret: string) => {
    if (!text) return "";
    const key = secret || DEFAULT_SECRET;
    return CryptoJS.AES.encrypt(text, key).toString();
};

export const decryptData = (ciphertext: string, secret: string) => {
    if (!ciphertext) return "";
    const key = secret || DEFAULT_SECRET;
    try {
        const bytes = CryptoJS.AES.decrypt(ciphertext, key);
        return bytes.toString(CryptoJS.enc.Utf8);
    } catch (e) {
        console.error("Decryption failed", e);
        return "";
    }
};

// Internal Helper for UI to load settings (decrypting keys)
// Actually `getStoredSettings` above acts as the loader and does NOT decrypt? 
// Wait, my logic above in `getStoredSettings` migration:
// "Let's standardise: `getStoredSettings` returns data ready for UI (so decrypted keys)."
//
// BUT for `v2` path:
// `const data = localStorage.getItem(STORAGE_KEY); return JSON.parse(data);` 
// This returns ENCRYPTED keys because `saveSettings` encrypts them.
//
// I need `loadSettingsForUI` vs `getRawSettings`.

export const loadSettings = (): GlobalSettings => {
    const raw = getStoredSettings(); // This gets raw JSON from storage (Encrypted keys)
    // Wait, getStoredSettings implementation above has mixed logic. 
    // The Migration path returns DECRYPTED keys.
    // The V2 path returns ENCRYPTED keys (if `JSON.parse` is just raw).
    
    // Let's Clean Up.
    // `getStoredSettings` -> Returns Raw Object from Storage (keys are ENCRYPTED).
    // `loadSettings()` -> Returns Object with DECRYPTED keys for UI.
    
    // REDEFINING getStoredSettings behavior correctly below:
    if (typeof window === 'undefined') return { encryption_secret: '', configs: [] };
    
    // V2
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
        return JSON.parse(data); // Keys are ENCRYPTED here
    }
    
    // V1 Migration (One time read)
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy) {
         try {
            const parsed = JSON.parse(legacy);
            // Return in V2 structure but with ENCRYPTED key (legacy key was stored encrypted)
            return {
                encryption_secret: parsed.encryption_secret || '',
                configs: parsed.api_key ? [{
                    id: 'legacy',
                    name: 'Default Provider',
                    provider: parsed.provider,
                    api_key: parsed.api_key, // Already encrypted
                    base_url: parsed.base_url,
                    model: parsed.model,
                    is_default: true
                }] : []
            };
        } catch (e) { return { encryption_secret: '', configs: [] }; }
    }
    
    return { encryption_secret: '', configs: [] };
};

// Helper to decrypt for UI
export const getDecryptedSettings = (): GlobalSettings => {
    const stored = loadSettings(); // Encrypted
    const secret = stored.encryption_secret || '';
    
    return {
        ...stored,
        configs: stored.configs.map(c => ({
            ...c,
            api_key: decryptData(c.api_key, secret)
        }))
    };
}
